import React from 'react';
import { TradeMarker, SessionData } from '../types';
import { useDraggable } from '../hooks/useDraggle';
import { formatTimeframe } from '../utils/timeframe';
import { formatBrokerTimeShort } from '../utils/timezone';

interface CandleInfo {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CombinedPanelProps {
  trades: TradeMarker[];
  sessionData: SessionData;
  selectedTool: string;
  onToolSelect: (tool: string) => void;
  hoveredCandle: CandleInfo | null;
}

const CombinedPanel: React.FC<CombinedPanelProps> = ({ 
  trades, 
  sessionData, 
  selectedTool, 
  onToolSelect,
  hoveredCandle
}) => {
  const { position, isDragging, dragRef, handleRef } = useDraggable({ x: 5, y: 5 });

  const formatPrice = (price: number) => {
    return price.toFixed(5);
  };

  const formatTime = (timestamp: number | string) => {
    const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
    return date.toLocaleTimeString();
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000).toFixed(1)}K`;
    }
    return volume.toString();
  };


  const getTradeStats = () => {
    const buyTrades = trades.filter(t => t.type === 'BUY').length;
    const sellTrades = trades.filter(t => t.type === 'SELL').length;
    const tpHits = trades.filter(t => t.type === 'TP').length;
    const slHits = trades.filter(t => t.type === 'SL').length;

    return { buyTrades, sellTrades, tpHits, slHits };
  };

  const tools = [
    { id: 'cursor', icon: '↖', title: 'Cursor' },
    { id: 'horizontal_line', icon: '─', title: 'Horizontal Line' },
    { id: 'vertical_line', icon: '│', title: 'Vertical Line' },
    { id: 'trend_line', icon: '⟋', title: 'Trend Line' },
    { id: 'rectangle', icon: '□', title: 'Rectangle' },
  ];

  const stats = getTradeStats();

  return (
    <div 
      ref={dragRef as React.RefObject<HTMLDivElement>}
      className={`trade-panel ${isDragging ? 'dragging' : ''}`}
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1000,
      }}
    >
      <div 
        ref={handleRef as React.RefObject<HTMLDivElement>}
        className="trade-panel-header draggable-handle"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <span>🎯 Trading Tools & Activity</span>
        <span className="drag-indicator">⋮⋮</span>
      </div>

      <div className="trade-panel-content">
        {/* Drawing Tools Section */}
        <div className="tools-section">
          <h4>Drawing Tools</h4>
          <div className="tools-grid">
            {tools.map(tool => (
              <button
                key={tool.id}
                className={`tool-button ${selectedTool === tool.id ? 'active' : ''}`}
                onClick={() => onToolSelect(tool.id)}
                title={tool.title}
              >
                {tool.icon}
              </button>
            ))}
          </div>
        </div>

        {/* OHLC Display Section */}
        <div className="ohlc-section">
          <h4>Candle Info</h4>
          <div className="ohlc-data">
            {hoveredCandle ? (
              <>
                <div className="ohlc-time">
                  {formatBrokerTimeShort(hoveredCandle.time)}
                </div>
                <div className="ohlc-grid">
                  <div className="ohlc-item">
                    <span className="ohlc-label">Open:</span>
                    <span className="ohlc-value">{formatPrice(hoveredCandle.open)}</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">High:</span>
                    <span className="ohlc-value high">{formatPrice(hoveredCandle.high)}</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">Low:</span>
                    <span className="ohlc-value low">{formatPrice(hoveredCandle.low)}</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">Close:</span>
                    <span className={`ohlc-value ${hoveredCandle.close >= hoveredCandle.open ? 'up' : 'down'}`}>
                      {formatPrice(hoveredCandle.close)}
                    </span>
                  </div>
                  <div className="ohlc-item volume-item">
                    <span className="ohlc-label">Volume:</span>
                    <span className="ohlc-value">{formatVolume(hoveredCandle.volume)}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="ohlc-time">
                  --:--:--
                </div>
                <div className="ohlc-grid">
                  <div className="ohlc-item">
                    <span className="ohlc-label">Open:</span>
                    <span className="ohlc-value">---.-----</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">High:</span>
                    <span className="ohlc-value">---.-----</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">Low:</span>
                    <span className="ohlc-value">---.-----</span>
                  </div>
                  <div className="ohlc-item">
                    <span className="ohlc-label">Close:</span>
                    <span className="ohlc-value">---.-----</span>
                  </div>
                  <div className="ohlc-item volume-item">
                    <span className="ohlc-label">Volume:</span>
                    <span className="ohlc-value">---</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Trade Statistics */}
        <div className="trade-stats">
          <h4>Trade Statistics</h4>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-value buy">{stats.buyTrades}</span>
              <span className="stat-label">Buy Orders</span>
            </div>
            <div className="stat-item">
              <span className="stat-value sell">{stats.sellTrades}</span>
              <span className="stat-label">Sell Orders</span>
            </div>
            <div className="stat-item">
              <span className="stat-value tp">{stats.tpHits}</span>
              <span className="stat-label">TP Hits</span>
            </div>
            <div className="stat-item">
              <span className="stat-value sl">{stats.slHits}</span>
              <span className="stat-label">SL Hits</span>
            </div>
          </div>
        </div>

        {/* Recent Trades */}
        <div className="recent-trades">
          <h4>Recent Trades ({trades.length})</h4>
          <div className="trades-list">
            {trades.slice(0, 5).map((trade, index) => (
              <div key={`${trade.ticket}_${index}`} className="trade-item">
                <div className="trade-info">
                  <span className={`trade-type ${trade.type.toLowerCase()}`}>
                    {trade.type}
                  </span>
                  <span className="trade-price">
                    {formatPrice(trade.price)}
                  </span>
                </div>
                <div className="trade-details">
                  <span className="trade-size">{trade.lot_size} lots</span>
                  <span className="trade-time">
                    {formatTime(trade.timestamp || trade.time)}
                  </span>
                </div>
              </div>
            ))}
            
            {trades.length === 0 && (
              <div className="no-trades">
                No trades yet. Waiting for MT5 activity...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CombinedPanel;